import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Footer from "./Footer";
import config from "../config";

const { API_BASE, UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json();
}

/* ── Parse description into { intro, features[] } ── */
function parseDescription(raw = "") {
  if (!raw) return { intro: "", features: [] };

  const lines = raw.split("\n");
  const features = [];
  let introLines = [];
  let currentTitle = null;
  let currentDesc = [];
  let inFeatures = false;

  const isTitleLine = (line) => {
    const t = line.trim();
    return t.endsWith(":") && t.length < 60 && !t.includes(". ");
  };

  const isProcessHeaderLine = (line) => {
    const t = line.trim();
    return /^our\s+.+\sprocess\.?$/i.test(t) && t.length < 60;
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    if (isTitleLine(trimmed)) {
      inFeatures = true;
      if (currentTitle !== null) {
        features.push({ label: currentTitle, desc: currentDesc.join(" ").trim() });
      }
      currentTitle = trimmed.replace(/:$/, "").trim();
      currentDesc = [];
    } else if (inFeatures) {
      currentDesc.push(trimmed);
    } else if (isProcessHeaderLine(trimmed)) {
      continue;
    } else {
      introLines.push(trimmed);
    }
  }

  if (currentTitle !== null) {
    features.push({ label: currentTitle, desc: currentDesc.join(" ").trim() });
  }

  return { intro: introLines.join(" ").trim(), features };
}

/* ── Extract a YouTube video ID from any common URL shape ── */
function getYouTubeId(url = "") {
  const match = String(url).match(
    /(?:youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match ? match[1] : null;
}

/* ── Breadcrumb ── */
function Breadcrumb({ parent, serviceTitle }) {
  return (
    <nav className="sp-bread" aria-label="Breadcrumb">
      <a href="/" className="sp-bread__link">Home</a>
      <span className="sp-bread__sep" aria-hidden="true">›</span>
      {parent && (
        <>
          <a href={`/${parent.link}`} className="sp-bread__link">{parent.label}</a>
          <span className="sp-bread__sep" aria-hidden="true">›</span>
        </>
      )}
      <span className="sp-bread__current">{serviceTitle}</span>
    </nav>
  );
}

/* ── Downloads block (sidebar) ── */
function Downloads({ products }) {
  if (!products || products.length === 0) return null;

  return (
    <div className="sp-side__downloads">
      <h3 className="sp-side__downloads-title">Downloads</h3>
      <p className="sp-side__downloads-desc">
        Brochures for our available products
      </p>
      <div className="sp-side__downloads-list">
        {products.map((product) => (
          <div key={product.slug} className="sp-side__dl-row">
            <span className="sp-side__dl-row-label" title={product.title}>
              {product.title}
            </span>
            <div className="sp-side__dl-row-actions">
              <a
                href={product.brochure_url}
                target="_blank"
                rel="noopener noreferrer"
                className="sp-side__dl-icon-btn sp-side__dl-icon-btn--view"
                aria-label={`View brochure for ${product.title}`}
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M10 4.5C6 4.5 2.5 10 2.5 10S6 15.5 10 15.5 17.5 10 17.5 10 14 4.5 10 4.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                  <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.6"/>
                </svg>
              </a>
              <a
                href={product.brochure_url}
                download
                className="sp-side__dl-icon-btn sp-side__dl-icon-btn--download"
                aria-label={`Download brochure for ${product.title}`}
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M10 3v10m0 0-3.5-3.5M10 13l3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 15.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Sidebar ── */
function Sidebar({ siblings, currentSlug, contact, brochureProducts }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* Mobile pull-tab: visible only on small screens */}
      <button
        className="sp-side__mobile-tab"
        onClick={() => setMobileOpen((v) => !v)}
        aria-label={mobileOpen ? "Close services menu" : "Open services menu"}
        aria-expanded={mobileOpen}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className={`sp-side__mobile-tab-icon ${mobileOpen ? "sp-side__mobile-tab-icon--open" : ""}`}
        >
          <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="sp-side__mobile-tab-label">Services</span>
      </button>

      {/* Overlay behind open services panel on mobile */}
      {mobileOpen && (
        <div
          className="sp-side__overlay"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside className="sp-side">

        {/* Service list */}
        {siblings.length > 0 && (
          <div className={`sp-side__block sp-side__services ${mobileOpen ? "sp-side__services--open" : ""}`}>
            <ul className="sp-side__list">
              {siblings.map((s) => {
                const active = s.link === currentSlug;
                return (
                  <li key={s.link}>
                    <a
                      href={`/services/${s.link}`}
                      className={`sp-side__item ${active ? "sp-side__item--active" : ""}`}
                    >
                      <span>{s.label}</span>
                      <span className={`sp-side__arrow ${active ? "sp-side__arrow--active" : ""}`}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                          <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8"
                            strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Contact Info */}
        {contact && (
          <div className="sp-side__block sp-side__contact">
            <h3 className="sp-side__contact-title">Contact Info</h3>
            <ul className="sp-side__contact-list">
              {contact.physical_address && (
                <li className="sp-side__contact-item">
                  <span className="sp-side__contact-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                      <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                    </svg>
                  </span>
                  <span>{contact.physical_address}</span>
                </li>
              )}
              {contact.phone_number && (
                <li className="sp-side__contact-item">
                  <span className="sp-side__contact-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                      <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328z"/>
                    </svg>
                  </span>
                  <a href={`tel:${contact.phone_number}`}>{contact.phone_number}</a>
                </li>
              )}
              {contact.email_address && (
                <li className="sp-side__contact-item">
                  <span className="sp-side__contact-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                      <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
                    </svg>
                  </span>
                  <a href={`mailto:${contact.email_address}`}>{contact.email_address}</a>
                </li>
              )}
            </ul>
          </div>
        )}

        {/* CTA card */}
        <div className="sp-side__cta">
          <div className="sp-side__cta-overlay" />
          <div className="sp-side__cta-content">
            <div className="sp-side__cta-box">
              <div className="sp-side__cta-icon">
                <img
                  src={`${UPLOADS_URL}/svg-icons/air-conditioner.svg`}
                  alt=""
                  aria-hidden="true"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
              <h3 className="sp-side__cta-heading">Get The Best Air Conditioning Services</h3>
              <p className="sp-side__cta-body">Our qualified technicians ensure every installation is completed safely, efficiently, and according to manufacturer specifications for maximum performance and energy efficiency</p>
              <a href="/contact-us" className="sp-side__cta-btn">CONTACT US</a>
            </div>
          </div>
        </div>

        {/* Downloads */}
        <Downloads products={brochureProducts} />

      </aside>
    </>
  );
}

/* ── Feature icon files ── */
const ICON_FILES = [
  "ac-install.svg",
  "dust.svg",
  "emergency-repair.svg",
  "heating.svg",
];

const DEFAULT_FEATURES = [
  { label: "Quality Materials", desc: "We use only the highest quality materials and parts for every job we undertake." },
  { label: "Latest Equipment",  desc: "Our team is equipped with the latest tools and technology for superior results." },
  { label: "Expert Technician", desc: "All our technicians are fully trained, experienced and certified professionals." },
  { label: "We're Certified",   desc: "Fully accredited and certified to deliver professional AC and HVAC services." },
];

/* ── Feature icons grid ── */
function Features({ features }) {
  const items = features && features.length ? features : DEFAULT_FEATURES;
  return (
    <div className="feat__grid">
      {items.map((f, i) => (
        <div key={i} className="feat__card">
          <div className="feat__icon">
            <img
              src={`${UPLOADS_URL}/svg-icons/${ICON_FILES[i % ICON_FILES.length]}`}
              alt=""
              aria-hidden="true"
              onError={(e) => { e.currentTarget.style.opacity = "0"; }}
            />
          </div>
          <div className="feat__body">
            <h4 className="feat__title">{f.label}</h4>
            <p className="feat__desc">{f.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── FAQ accordion ── */
function Faqs({ faqs }) {
  const [open, setOpen] = useState(null);
  if (!faqs?.length) return null;
  return (
    <div className="sp-faq">
      <h3 className="sp-faq__heading">Read Useful FAQs</h3>
      {faqs.map((faq, i) => (
        <div key={faq.id} className={`sp-faq__item ${open === i ? "sp-faq__item--open" : ""}`}>
          <button
            className="sp-faq__q"
            onClick={() => setOpen(open === i ? null : i)}
            aria-expanded={open === i}
          >
            {faq.title}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"
              className={`sp-faq__chev ${open === i ? "sp-faq__chev--open" : ""}`}>
              <path d="M2 4.5l5 5 5-5" stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {open === i && (
            <div className="sp-faq__a">{faq.description}</div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Video card ── */
function VideoCard({ video }) {
  const [playing, setPlaying] = useState(false);

  if (!video) {
    return (
      <div className="sp-video sp-video--empty">
        <span>No video available for this service yet.</span>
      </div>
    );
  }

  const videoId = getYouTubeId(video.video_url);

  const handlePlay = () => {
    if (videoId) {
      setPlaying(true);
    } else if (video.video_url) {
      window.open(video.video_url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="sp-video-wrap">
      <div className="sp-video">
        {playing && videoId ? (
          <iframe
            className="sp-video__frame"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title={video.title || "Service video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            className="sp-video__thumb"
            onClick={handlePlay}
            aria-label={`Play video: ${video.title || "Service video"}`}
          >
            {video.thumbnail_filename ? (
              <img
                src={`${UPLOADS_URL}/videos/${video.thumbnail_filename}`}
                alt={video.title || "Service video thumbnail"}
                className="sp-video__thumb-img"
              />
            ) : (
              <div className="sp-video__thumb-fallback" />
            )}
            <span className="sp-video__play" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </button>
        )}
      </div>
      {video.description && (
        <p className="sp-video__desc">{video.description}</p>
      )}
    </div>
  );
}

/* ── Lightbox ── */
function Lightbox({ images, index, onClose, onNavigate }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNavigate(1);
      if (e.key === "ArrowLeft") onNavigate(-1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, onNavigate]);

  return (
    <div className="sp-lightbox" onClick={onClose} role="dialog" aria-modal="true">
      <button className="sp-lightbox__close" onClick={onClose} aria-label="Close">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
          <path d="M4 4l12 12M16 4L4 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      {images.length > 1 && (
        <button
          className="sp-lightbox__nav sp-lightbox__nav--prev"
          onClick={(e) => { e.stopPropagation(); onNavigate(-1); }}
          aria-label="Previous image"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 4l-6 6 6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <img
        src={`${UPLOADS_URL}/services/${images[index]}`}
        alt={`Gallery item ${index + 1}`}
        className="sp-lightbox__img"
        onClick={(e) => e.stopPropagation()}
      />

      {images.length > 1 && (
        <button
          className="sp-lightbox__nav sp-lightbox__nav--next"
          onClick={(e) => { e.stopPropagation(); onNavigate(1); }}
          aria-label="Next image"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M8 4l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {images.length > 1 && (
        <div className="sp-lightbox__counter">{index + 1} / {images.length}</div>
      )}
    </div>
  );
}

/* ── Gallery grid ── */
function GalleryGrid({ images, service, video }) {
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (i) => {
    setLightboxIndex(i);
    setLightboxOpen(true);
  };

  const navigateLightbox = (dir) => {
    setLightboxIndex((i) => (i + dir + images.length) % images.length);
  };

  return (
    <div className="sp-gallery">

      <div className="sp-gallery__video-col">
        <VideoCard video={video} />
      </div>

      <div className="sp-gallery__media">
        {images.length > 0 ? (
          <>
            <button
              type="button"
              className="sp-gallery__img-wrap"
              onClick={() => openLightbox(active)}
              aria-label="View larger image"
            >
              <img
                src={`${UPLOADS_URL}/services/${images[active]}`}
                alt={service.title}
                className="sp-gallery__img"
              />
              <span className="sp-gallery__zoom" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <circle cx="8.5" cy="8.5" r="6" stroke="#fff" strokeWidth="1.8" />
                  <path d="M13 13l4.5 4.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
            </button>

            {images.length > 1 && (
              <div className="sp-gallery__thumbs">
                {images.map((img, i) => (
                  <button
                    key={i}
                    className={`sp-gallery__thumb ${i === active ? "sp-gallery__thumb--active" : ""}`}
                    onClick={() => setActive(i)}
                    aria-label={`View image ${i + 1}`}
                  >
                    <img src={`${UPLOADS_URL}/services/${img}`} alt={`Thumbnail ${i + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="sp-gallery__media-empty">No gallery images available.</div>
        )}
      </div>

      {lightboxOpen && images.length > 0 && (
        <Lightbox
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onNavigate={navigateLightbox}
        />
      )}
    </div>
  );
}

/* ── Main Page ── */
function ServicePage() {
  const { slug } = useParams();

  const [data,    setData]    = useState(null);
  const [contact, setContact] = useState(null);
  const [faqs,    setFaqs]    = useState([]);
  const [video,   setVideo]   = useState(null);
  const [brochureProducts, setBrochureProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchJSON(`${API_BASE}/service-detail.php?slug=${encodeURIComponent(slug)}`),
      fetchJSON(`${API_BASE}/company-contact.php`),
      fetchJSON(`${API_BASE}/faq.php`).catch(() => []),
      fetchJSON(`${API_BASE}/products.php`).catch(() => []),
    ])
      .then(([svcData, contactData, faqData, productsData]) => {
        if (cancelled) return null;
        setData(svcData);
        setContact(contactData);
        setFaqs(Array.isArray(faqData) ? faqData.filter(f => f.active !== 0) : []);
        setBrochureProducts(
          Array.isArray(productsData) ? productsData.filter((p) => p.brochure_url) : []
        );

        const title = svcData?.service?.title;
        if (!title) return null;

        return fetchJSON(`${API_BASE}/video-by-title.php?title=${encodeURIComponent(title)}`)
          .catch(() => null);
      })
      .then((videoData) => {
        if (cancelled) return;
        setVideo(videoData || null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("ServicePage load error:", err);
        setError("Failed to load service.");
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug]);

  if (loading) return null;

  if (error || !data?.service) return (
    <div className="sp-error">
      <h2>Service not found</h2>
      <p>The service you are looking for does not exist or has been removed.</p>
      <a href="/" className="sp-error__link">Back to Home</a>
    </div>
  );

  const { service, siblings, parent } = data;
  const { intro, features } = parseDescription(service.description);

  const galleryImages = [
    service.image3, service.image4, service.image5,
    service.image6, service.image7, service.image8,
  ].filter(Boolean);

  return (
    <>
    <div className="sp">

      {/* ── HERO ── */}
      <div className="sp-hero">
        {service.image1 && (
          <img
            src={`${UPLOADS_URL}/services/${service.image1}`}
            alt={service.title}
            className="sp-hero__img"
          />
        )}
        <div className="sp-hero__overlay" />
        <div className="sp-hero__content">
          <Breadcrumb parent={parent} serviceTitle={service.title} />
          <h1 className="sp-hero__title">{service.title}</h1>
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="sp-body">
        <div className="sp-body__inner">

          <Sidebar
            siblings={siblings}
            currentSlug={slug}
            contact={contact}
            brochureProducts={brochureProducts}
          />

          <main className="sp-main">

            <div className="sp-top">
              <div className="sp-top__text">
                <h2 className="sp-main__heading">VyberTech's {service.title}</h2>
                <div className="sp-main__desc">
                  {intro
                    ? <p>{intro}</p>
                    : <p>We provide professional {service.title} services with years of experience and commitment to quality.</p>
                  }
                </div>
              </div>
              {service.image2 && (
                <div className="sp-top__img-wrap">
                  <img
                    src={`${UPLOADS_URL}/services/${service.image2}`}
                    alt={service.title}
                    className="sp-top__img"
                  />
                </div>
              )}
            </div>

            <div className="sp-section">
              <h3 className="sp-main__subheading">Our {service.title} Process</h3>
              <Features features={features} />
            </div>

            <div className="sp-section">
              <h3 className="sp-main__subheading">Ultimate Service Provider</h3>
              {(galleryImages.length > 0 || video) && (
                <GalleryGrid images={galleryImages} service={service} video={video} />
              )}
            </div>

            <Faqs faqs={faqs} />

          </main>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800;900&display=swap');

        /* ── RESET ── */
        .sp *, .sp *::before, .sp *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .sp { overflow-x: hidden; width: 100%; }
        .sp a { text-decoration: none; }
        .sp ul { list-style: none; }
        .sp { font-family: 'Roboto', sans-serif; background: #f5f7fa; }

        /* ── ERROR ── */
        .sp-error {
          text-align: center; padding: 80px 24px;
          font-family: 'Roboto', sans-serif; color: #424244;
        }
        .sp-error h2 { font-size: 28px; color: #013d97; margin-bottom: 12px; }
        .sp-error p  { margin-bottom: 24px; }
        .sp-error__link {
          display: inline-block; padding: 10px 24px; background: #029add;
          color: #fff; border-radius: 6px; font-weight: 700;
        }

        /* ── HERO ── */
        .sp-hero { position: relative; height: 280px; overflow: hidden; background: #013d97; }
        .sp-hero__img { width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
        .sp-hero__overlay {
          position: absolute; inset: 0;
          background: linear-gradient(to right, rgba(1,21,70,.75) 40%, rgba(1,61,151,.4) 100%);
        }
        .sp-hero__content {
          position: absolute; inset: 0; display: flex; flex-direction: column;
          justify-content: flex-end; padding: 32px 40px;
          max-width: 1280px; margin: 0 auto; left: 0; right: 0;
        }
        .sp-hero__title { font-size: clamp(26px, 3.5vw, 44px); font-weight: 900; color: #fff; line-height: 1.15; margin-top: 10px; }

        /* ── BREADCRUMB ── */
        .sp-bread { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .sp-bread__link { font-size: 13px; font-weight: 600; color: rgba(255,255,255,.8); background: #029add; padding: 3px 10px; border-radius: 3px; transition: background .2s; }
        .sp-bread__link:hover { background: #013d97; }
        .sp-bread__sep { color: rgba(255,255,255,.6); font-size: 14px; }
        .sp-bread__current { font-size: 13px; color: rgba(255,255,255,.7); }

        /* ── BODY LAYOUT ── */
        .sp-body { padding: 48px 0 72px; }
        .sp-body__inner {
          max-width: 1280px; margin: 0 auto; padding: 0 40px;
          display: grid; grid-template-columns: 280px 1fr;
          gap: 32px; align-items: start;
        }

        /* ── MOBILE PULL-TAB — hidden on desktop ── */
        .sp-side__mobile-tab { display: none; }

        /* ── MOBILE OVERLAY — hidden on desktop ── */
        .sp-side__overlay { display: none; }

        /* ── SIDEBAR ── */
        .sp-side { display: flex; flex-direction: column; gap: 0; }
        .sp-side__block { background: #fff; overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,.07); margin-bottom: 24px; }

        /* Service list */
        .sp-side__list { display: flex; flex-direction: column; }
        .sp-side__item {
          display: flex; align-items: center; justify-content: space-between;
          padding: 13px 0 13px 18px; font-size: 14px; font-weight: 600;
          color: #424244; border-bottom: 1px solid #f0f2f7;
          transition: color .2s; gap: 0;
        }
        .sp-side__item:last-child { border-bottom: none; }
        .sp-side__item:hover { color: #029add; }
        .sp-side__item--active { color: #029add; background: #f5f7fa; }
        .sp-side__arrow {
          display: flex; align-items: center; justify-content: center;
          width: 44px; height: 44px; flex-shrink: 0;
          background: #029add; color: #fff;
          transition: background .2s;
        }
        .sp-side__arrow--active { background: #013d97; }
        .sp-side__item:hover .sp-side__arrow { background: #013d97; }

        /* Contact info */
        .sp-side__contact { padding: 20px; margin-bottom: 24px; }
        .sp-side__contact-title {
          font-size: 16px; font-weight: 800; color: #1a1a2e;
          margin-bottom: 16px; padding-bottom: 12px;
          border-bottom: 1px solid #e0e4ef;
        }
        .sp-side__contact-list { display: flex; flex-direction: column; gap: 14px; }
        .sp-side__contact-item {
          display: flex; align-items: flex-start; gap: 12px;
          font-size: 13px; color: #424244; line-height: 1.5;
        }
        .sp-side__contact-item a { color: #424244; transition: color .2s; }
        .sp-side__contact-item a:hover { color: #029add; }
        .sp-side__contact-icon {
          width: 30px; height: 30px; border-radius: 50%;
          background: #fff3ee; display: flex; align-items: center;
          justify-content: center; color: #029add; flex-shrink: 0;
        }

        /* ── CTA card ── */
        .sp-side__cta {
          position: relative; overflow: hidden;
          min-height: 260px;
          background: linear-gradient(135deg, rgba(1,21,70,1) 0%, rgba(1,61,151,1) 100%);
          margin-bottom: 24px;
        }
        .sp-side__cta-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(1,21,70,.88) 0%, rgba(1,61,151,.75) 100%);
        }
        .sp-side__cta-content { position: relative; z-index: 1; padding: 24px 20px; }
        .sp-side__cta-box {
          border: 2px solid rgba(255,255,255,.4);
          padding: 22px 18px;
          display: flex; flex-direction: column; gap: 14px;
        }
        .sp-side__cta-icon {
          width: 52px; height: 52px;
          display: flex; align-items: center; justify-content: center;
        }
        .sp-side__cta-icon img {
          width: 46px; height: 46px; object-fit: contain;
          filter: brightness(0) invert(1);
        }
        .sp-side__cta-heading { font-size: 16px; font-weight: 800; color: #fff; line-height: 1.35; text-align: center; }
        .sp-side__cta-body { font-size: 13px; color: rgba(255,255,255,.72); line-height: 1.6; text-align: justify; }
        .sp-side__cta-btn {
          display: inline-block; background: #029add; color: #fff;
          font-size: 11px; font-weight: 800; letter-spacing: .1em;
          text-transform: uppercase; padding: 10px 20px;
          align-self: flex-start; transition: background .2s;
        }
        .sp-side__cta-btn:hover { background: #022d75; }

        /* ── DOWNLOADS card ── */
        .sp-side__downloads {
          background: #fff;
          border: 2px dashed rgba(1,61,151,.25);
          border-radius: 4px;
          padding: 22px 20px;
          margin-bottom: 24px;
        }
        .sp-side__downloads-title { font-size: 20px; font-weight: 800; color: #1a1a2e; margin-bottom: 10px; }
        .sp-side__downloads-desc { font-size: 13px; color: #8890a0; line-height: 1.6; margin-bottom: 18px; }
        .sp-side__downloads-list { display: flex; flex-direction: column; gap: 10px; }

        .sp-side__dl-row {
          display: flex; align-items: center; justify-content: space-between;
          gap: 10px; padding: 10px 12px;
          background: #f9fafb; border: 1px solid #eef0f5; border-radius: 4px;
          transition: border-color .2s, background .2s;
        }
        .sp-side__dl-row:hover { background: #f0f8fd; border-color: #029add; }
        .sp-side__dl-row-label {
          font-size: 12.5px; font-weight: 700; color: #1a1a2e;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          flex: 1; min-width: 0;
        }
        .sp-side__dl-row-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
        .sp-side__dl-icon-btn {
          width: 28px; height: 28px; border-radius: 3px;
          display: flex; align-items: center; justify-content: center;
          transition: background .2s, color .2s, border-color .2s;
        }
        .sp-side__dl-icon-btn--view {
          color: #013d97; background: #fff; border: 1px solid #e0e4ef;
        }
        .sp-side__dl-icon-btn--view:hover { background: #eef3fb; border-color: #029add; }
        .sp-side__dl-icon-btn--download { color: #fff; background: #013d97; }
        .sp-side__dl-icon-btn--download:hover { background: #022d75; }

        /* ── MAIN CONTENT ── */
        .sp-main { display: flex; flex-direction: column; gap: 32px; min-width: 0; }
        .sp-section { background: #fff; padding: 32px; box-shadow: 0 2px 12px rgba(0,0,0,.06); }

        .sp-main__heading { font-size: clamp(20px, 2.2vw, 28px); font-weight: 800; color: #1a1a2e; margin-bottom: 16px; }
        .sp-main__subheading { font-size: 22px; font-weight: 800; color: #1a1a2e; margin-bottom: 16px; }
        .sp-main__desc { display: flex; flex-direction: column; gap: 12px; text-align: justify; }
        .sp-main__desc p { font-size: 14.5px; color: #5a5f72; line-height: 1.75; }

        /* TOP: text left, image2 right */
        .sp-top {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 32px; align-items: start;
          background: #fff; padding: 32px;
          box-shadow: 0 2px 12px rgba(0,0,0,.06);
        }
        .sp-top__img-wrap { border-radius: 4px; overflow: hidden; }
        .sp-top__img { width: 100%; height: 100%; object-fit: cover; display: block; border-radius: 4px; }

        /* ── FEATURE ICONS 2×2 ── */
        .feat__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .feat__card {
          display: flex; align-items: flex-start; gap: 16px;
          padding: 20px; background: #f9fafb;
          border: 1px solid #eef0f5; border-radius: 4px;
          transition: border-color .2s, background .2s;
        }
        .feat__card:hover { background: #f0f8fd; border-color: #029add; }
        .feat__icon {
          width: 52px; height: 52px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
        }
        .feat__icon img { width: 40px; height: 40px; object-fit: contain; }
        .feat__title { font-size: 15px; font-weight: 800; color: #1a1a2e; margin-bottom: 6px; }
        .feat__desc { font-size: 13px; color: #5a5f72; line-height: 1.6; }

        /* ── GALLERY ── */
        .sp-gallery { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; align-items: start; }
        .sp-gallery__media { display: flex; flex-direction: column; gap: 12px; }
        .sp-gallery__img-wrap {
          position: relative; display: block; width: 100%;
          border: none; padding: 0; background: none; cursor: zoom-in;
          border-radius: 4px; overflow: hidden; aspect-ratio: 4/3;
        }
        .sp-gallery__img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .35s; }
        .sp-gallery__img-wrap:hover .sp-gallery__img { transform: scale(1.05); }
        .sp-gallery__zoom {
          position: absolute; top: 12px; right: 12px;
          width: 34px; height: 34px; border-radius: 50%;
          background: rgba(1,21,70,.55);
          display: flex; align-items: center; justify-content: center;
        }
        .sp-gallery__media-empty {
          aspect-ratio: 4/3; display: flex; align-items: center; justify-content: center;
          text-align: center; padding: 20px; border-radius: 4px;
          background: #f9fafb; border: 1px dashed #d8dee9;
          color: #8890a0; font-size: 13px;
        }
        .sp-gallery__thumbs { display: flex; gap: 10px; flex-wrap: wrap; }
        .sp-gallery__thumb {
          width: 90px; height: 68px; border-radius: 4px; overflow: hidden;
          border: 2px solid transparent; cursor: pointer; padding: 0;
          background: #e6f0fb; flex-shrink: 0;
          transition: border-color .2s, transform .2s;
        }
        .sp-gallery__thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .sp-gallery__thumb:hover { transform: translateY(-2px); border-color: #029add; }
        .sp-gallery__thumb--active { border-color: #029add; }

        /* Video column */
        .sp-gallery__video-col { display: flex; flex-direction: column; }
        .sp-video-wrap { display: flex; flex-direction: column; width: 100%; }
        .sp-video {
          position: relative; width: 100%; aspect-ratio: 4/3;
          border-radius: 4px; overflow: hidden; background: #0b1530;
        }
        .sp-video__desc {
          padding: 14px 0 0; font-size: 13.5px;
          color: #5a5f72; line-height: 1.75; text-align: justify;
        }
        .sp-video--empty {
          display: flex; align-items: center; justify-content: center;
          text-align: center; padding: 20px;
          background: #f9fafb; border: 1px dashed #d8dee9;
          color: #8890a0; font-size: 13px;
        }
        .sp-video__frame { width: 100%; height: 100%; border: 0; display: block; }
        .sp-video__thumb { position: relative; width: 100%; height: 100%; border: none; padding: 0; cursor: pointer; display: block; }
        .sp-video__thumb-img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .sp-video__thumb-fallback { width: 100%; height: 100%; background: linear-gradient(135deg, #013d97, #029add); }
        .sp-video__play { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
        .sp-video__play svg {
          width: 60px; height: 60px; padding: 18px; border-radius: 50%;
          background: rgba(1,21,70,.6); transition: background .2s, transform .2s;
        }
        .sp-video__thumb:hover .sp-video__play svg { background: #013d97; transform: scale(1.08); }

        /* ── LIGHTBOX ── */
        .sp-lightbox {
          position: fixed; inset: 0; z-index: 1000;
          background: rgba(5,10,25,.92);
          display: flex; align-items: center; justify-content: center; padding: 40px;
        }
        .sp-lightbox__img {
          max-width: min(90vw, 1100px); max-height: 85vh;
          object-fit: contain; border-radius: 4px;
          box-shadow: 0 10px 40px rgba(0,0,0,.5);
        }
        .sp-lightbox__close {
          position: absolute; top: 20px; right: 20px;
          width: 40px; height: 40px; border-radius: 50%;
          background: rgba(255,255,255,.12); border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: background .2s;
        }
        .sp-lightbox__close:hover { background: rgba(255,255,255,.25); }
        .sp-lightbox__nav {
          position: absolute; top: 50%; transform: translateY(-50%);
          width: 48px; height: 48px; border-radius: 50%;
          background: rgba(255,255,255,.12); border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: background .2s;
        }
        .sp-lightbox__nav:hover { background: rgba(255,255,255,.25); }
        .sp-lightbox__nav--prev { left: 20px; }
        .sp-lightbox__nav--next { right: 20px; }
        .sp-lightbox__counter {
          position: absolute; bottom: 24px; left: 50%; transform: translateX(-50%);
          color: #fff; font-size: 13px; background: rgba(255,255,255,.12);
          padding: 6px 14px; border-radius: 20px;
        }

        /* ── FAQS ── */
        .sp-faq { background: #fff; padding: 32px; box-shadow: 0 2px 12px rgba(0,0,0,.06); }
        .sp-faq__heading { font-size: 22px; font-weight: 800; color: #1a1a2e; margin-bottom: 20px; }
        .sp-faq__item { border: 1px solid #e8eaf0; border-radius: 4px; margin-bottom: 10px; overflow: hidden; }
        .sp-faq__q {
          width: 100%; display: flex; align-items: center; justify-content: space-between;
          gap: 12px; padding: 14px 18px; background: #f5f7fa; border: none;
          cursor: pointer; text-align: left; font-size: 14px; font-weight: 700;
          color: #1a1a2e; transition: background .2s, color .2s;
        }
        .sp-faq__q:hover { background: #e6f4fc; }
        .sp-faq__item--open .sp-faq__q { background: #013d97; color: #fff; }
        .sp-faq__chev { flex-shrink: 0; transition: transform .25s; }
        .sp-faq__chev--open { transform: rotate(180deg); }
        .sp-faq__item--open .sp-faq__chev { stroke: #fff; }
        .sp-faq__a { padding: 14px 18px; font-size: 13.5px; color: #5a5f72; line-height: 1.7; background: #fff; border-top: 1px solid #e8eaf0; }

        /* ══════════════════════════════════════════
           RESPONSIVE
        ══════════════════════════════════════════ */

        @media (max-width: 1024px) {
          .sp-body__inner { grid-template-columns: 240px 1fr; }
        }

        /* ── TABLET / MOBILE: single column ── */
        @media (max-width: 768px) {

          /* Body: single column, normal padding — identical approach to PortfolioPage */
          .sp-body__inner {
            grid-template-columns: 1fr;
            padding: 0 20px;
            /* main first, then sidebar below */
            grid-template-areas:
              "main"
              "sidebar";
          }
          .sp-side { grid-area: sidebar; }
          .sp-main { grid-area: main; }

          /* Services list inside sidebar: hidden by default on mobile */
          .sp-side__services { display: none; margin-bottom: 0; }

          /* When open: fixed panel slides in from left, centred vertically */
          .sp-side__services--open {
            display: block;
            position: fixed;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 280px;
            max-width: calc(100vw - 24px);
            max-height: 75vh;
            z-index: 300;
            overflow-y: auto;
            background: #f5f7fa;
            padding: 20px 16px 40px;
            border-radius: 0 12px 12px 0;
            box-shadow: 4px 0 24px rgba(0,0,0,.2);
            margin-bottom: 0;
          }

          /* Dimmed overlay behind the open panel */
          .sp-side__overlay {
            display: block;
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,.4);
            z-index: 200;
          }

          /* Pull-tab: fixed to the left edge, centred vertically */
          .sp-side__mobile-tab {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 6px;
            position: fixed;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            z-index: 400;
            background: #013d97;
            color: #fff;
            border: none;
            border-radius: 0 6px 6px 0;
            padding: 14px 10px 14px 8px;
            cursor: pointer;
            box-shadow: 3px 0 12px rgba(0,0,0,.25);
            writing-mode: vertical-rl;
            text-orientation: mixed;
            transition: background .2s;
          }
          .sp-side__mobile-tab:hover { background: #029add; }
          .sp-side__mobile-tab-icon {
            width: 16px; height: 16px;
            transition: transform .3s;
          }
          .sp-side__mobile-tab-icon--open { transform: rotate(180deg); }
          .sp-side__mobile-tab-label {
            font-size: 11px; font-weight: 800;
            letter-spacing: .1em; text-transform: uppercase;
            font-family: 'Roboto', sans-serif;
          }

          /* Hero */
          .sp-hero { height: 220px; }
          .sp-hero__content { padding: 24px 20px; }

          /* Two-col top section → single column */
          .sp-top { grid-template-columns: 1fr; padding: 20px; }

          /* Feature grid stays 2-col on tablet */
          .feat__grid { grid-template-columns: 1fr 1fr; }

          /* Gallery: stack video above images */
          .sp-gallery { grid-template-columns: 1fr; }

          /* Lightbox */
          .sp-lightbox { padding: 20px; }
          .sp-lightbox__nav { width: 40px; height: 40px; }
        }

        /* ── SMALL PHONES ── */
        @media (max-width: 480px) {
          .sp-body__inner { padding: 0 16px; }
          .feat__grid { grid-template-columns: 1fr; }
          .sp-top, .sp-section, .sp-faq { padding: 20px; }
        }

        /* ── FOCUS & MOTION ── */
        a:focus-visible, button:focus-visible { outline: 2px solid #029add; outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) {
          .sp *, .sp *::before, .sp *::after { transition-duration: 0ms !important; }
        }
      `}</style>
    </div>
    <Footer />
    </>
  );
}

export default ServicePage;